// apps/frontend/src/services/apiClient.ts
// 🚀 Service API Client Enterprise - Architecture Premium Optimisée

import i18n from '../i18n';
import { API_BASE_URL } from '../config/api';
import { isUserActive } from '../lib/userActivityTracker';

// ============================================================================
// 🎯 CONFIGURATION & CONSTANTES
// ============================================================================
const REQUEST_TIMEOUT = 30000; // 30 secondes
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_BASE = 1000; // 1 seconde

// ============================================================================
// 🎯 INTERFACES PREMIUM ENTERPRISE
// ============================================================================

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  code?: string;
  meta?: {
    requestId: string;
    timestamp: string;
    version: string;
    executionTime?: number;
  };
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  expiresIn: number;
}

interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
  role: string;
  isVerified: boolean;
  preferredLocale: string;
  timezone: string;
  countryCode: string;
  isPremium: boolean;
  isOnline: boolean;
  lastSeenAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface UserProfile {
  id: string;
  userId: string;
  name: string;
  age?: number;
  gender?: string;
  relationship: string;
  clusteringVector: Record<string, any>;
  styleFingerprint: Record<string, any>;
  colorPreferences: Record<string, any>;
  brandAffinity: Record<string, any>;
  priceSegment?: string;
  sizePreferences: Record<string, any>;
  culturalProfile: Record<string, any>;
  languagePreferences: string[];
  behaviorFingerprint: Record<string, any>;
  purchasePattern?: string;
  lifetimeValue: number;
  nextPurchaseWindow?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UserSession {
  id: string;
  sessionToken: string;
  deviceType: string;
  deviceName?: string;
  os?: string;
  browser?: string;
  ipAddress: string;
  ipCountry?: string;
  ipCity?: string;
  status: 'ACTIVE' | 'EXPIRED' | 'REVOKED';
  isCurrent: boolean;
  lastActivity: string;
  expiresAt: string;
  isSuspicious: boolean;
  riskScore?: number;
  userAgent: string;
  createdAt: string;
}

interface TwoFactorAuth {
  id: string;
  isEnabled: boolean;
  setupCompleted: boolean;
  recoveryEmail?: string;
  recoveryPhone?: string;
  backupCodesCount: number;
  lastUsed?: string;
  totalUses: number;
  enabledAt?: string;
}

interface ConnectionStatus {
  connected: boolean;
  url: string;
  message: string;
  latency?: number;
  version?: string;
  timestamp: string;
}

interface RequestOptions extends RequestInit {
  timeout?: number;
  retries?: number;
  skipAuth?: boolean;
  skipRefresh?: boolean;
}

// ============================================================================
// 🚀 API CLIENT ENTERPRISE CLASS
// ============================================================================

class ApiClient {
  private baseURL: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private refreshPromise: Promise<boolean> | null = null;
  private requestQueue: Array<() => void> = [];
  private isRefreshing: boolean = false;

  constructor() {
    this.baseURL = API_BASE_URL;
    this.accessToken = localStorage.getItem('glowp_access_token');
    this.refreshToken = localStorage.getItem('glowp_refresh_token');

    // Nettoyer les tokens expirés au démarrage
    this.validateStoredTokens();
  }

  // ============================================================================
  // 🔧 MÉTHODES UTILITAIRES PRIVÉES
  // ============================================================================

  private validateStoredTokens(): void {
    if (this.accessToken) {
      try {
        const payload = JSON.parse(atob(this.accessToken.split('.')[1]));
        const now = Math.floor(Date.now() / 1000);
        if (payload.exp && payload.exp < now) {
          this.clearTokens();
        }
      } catch (error) {
        console.error('Erreur validation token:', error);
        this.clearTokens();
      }
    }
  }

  private async createTimeoutController(
    timeout: number
  ): Promise<AbortController> {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), timeout);
    return controller;
  }

  private async delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private calculateRetryDelay(attempt: number): number {
    return RETRY_DELAY_BASE * Math.pow(2, attempt - 1) + Math.random() * 1000;
  }

  private getHeaders(
    excludeContentType: boolean = false
  ): Record<string, string> {
    const headers: Record<string, string> = {
      'Accept-Language': i18n.language || 'fr-FR',
      'X-Request-ID': crypto.randomUUID(),
      'X-Client-Version': '1.0.0',
      'X-Platform': 'web',
    };

    if (!excludeContentType) {
      headers['Content-Type'] = 'application/json';
    }

    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
    }

    // Signale au backend si l'user a vraiment interagi récemment (pas juste
    // un onglet ouvert). Le middleware ne bump last_activity_at que si ce
    // header vaut 'true' (ou si la méthode est une mutation).
    if (isUserActive()) {
      headers['X-User-Active'] = 'true';
    }

    return headers;
  }

  private async executeWithRetry<T>(
    operation: () => Promise<T>,
    maxRetries: number = MAX_RETRY_ATTEMPTS
  ): Promise<T> {
    let lastError: Error;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error: any) {
        lastError = error;

        // Ne pas retry sur certaines erreurs
        if (
          error.status === 401 ||
          error.status === 403 ||
          error.status === 422 ||
          error.status === 429 // Rate limit — retrying aggravates the problem
        ) {
          throw error;
        }

        if (attempt === maxRetries) {
          throw lastError;
        }

        const delay = this.calculateRetryDelay(attempt);
        await this.delay(delay);
      }
    }

    throw lastError!;
  }

  // ============================================================================
  // 🔄 GESTION REFRESH TOKEN INTELLIGENT
  // ============================================================================

  private async queueRequest(requestFn: () => void): Promise<void> {
    return new Promise(resolve => {
      this.requestQueue.push(() => {
        requestFn();
        resolve();
      });
    });
  }

  private processRequestQueue(): void {
    const queue = [...this.requestQueue];
    this.requestQueue = [];
    queue.forEach(request => request());
  }

  async refreshAccessToken(): Promise<boolean> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    if (!this.refreshToken) {
      return false;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.performTokenRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
      this.processRequestQueue();
    }
  }

  private async performTokenRefresh(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseURL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language || 'fr-FR',
        },
        body: JSON.stringify({
          refreshToken: this.refreshToken,
        }),
      });

      if (!response.ok) {
        throw new Error(`Refresh failed: ${response.status}`);
      }

      const data: ApiResponse<{ tokens: AuthTokens }> = await response.json();

      if (data.success && data.data?.tokens) {
        this.setTokens(
          data.data.tokens.accessToken,
          data.data.tokens.refreshToken
        );
        return true;
      } else {
        throw new Error('Invalid refresh response');
      }
    } catch (error) {
      console.error('❌ Erreur refresh token:', error);
      this.clearTokens();
      return false;
    }
  }

  // ============================================================================
  // 🌐 MÉTHODE REQUEST PRINCIPALE AMÉLIORÉE
  // ============================================================================

  async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    // POST/PATCH sont non-idempotents : un retry après timeout côté client
    // recrée la ressource si le serveur a déjà traité la requête (cf. doublons
    // d'annonces, messages, offres, commandes). Convention HTTP standard.
    // GET/PUT/DELETE restent retryables (idempotents).
    const httpMethod = (options.method || 'GET').toUpperCase();
    const isUnsafeMethod = httpMethod === 'POST' || httpMethod === 'PATCH';
    const defaultRetries = isUnsafeMethod ? 1 : MAX_RETRY_ATTEMPTS;

    const {
      timeout = REQUEST_TIMEOUT,
      retries = defaultRetries,
      skipAuth = false,
      skipRefresh = false,
      ...fetchOptions
    } = options;

    // Gestion FormData automatique
    const isFormData = fetchOptions.body instanceof FormData;
    const headers = this.getHeaders(isFormData);

    // CORRECTION SPRINT 8 : Supprimer Content-Type pour FormData
    if (isFormData) {
      delete headers['Content-Type'];
    }

    const requestFn = async (): Promise<ApiResponse<T>> => {
      const controller = await this.createTimeoutController(timeout);
      const url = `${this.baseURL}${endpoint}`;

      try {
        let response = await fetch(url, {
          ...fetchOptions,
          headers: {
            ...headers,
            ...fetchOptions.headers,
          },
          signal: controller.signal,
        });

        // Gestion refresh token automatique
        if (
          response.status === 401 &&
          !skipAuth &&
          !skipRefresh &&
          this.refreshToken &&
          endpoint !== '/api/v1/auth/refresh'
        ) {
          if (this.isRefreshing) {
            await this.queueRequest(() => {});
          }

          const refreshed = await this.refreshAccessToken();

          if (refreshed) {
            const newHeaders = this.getHeaders(isFormData);
            if (isFormData) {
              delete newHeaders['Content-Type'];
            }

            response = await fetch(url, {
              ...fetchOptions,
              headers: {
                ...newHeaders,
                ...fetchOptions.headers,
              },
              signal: controller.signal,
            });
          } else {
            console.warn('🚨 Refresh échoué - Redirection login nécessaire');
            this.clearTokens();

            // Dispatch event personnalisé pour gérer la déconnexion
            window.dispatchEvent(new CustomEvent('auth:session-expired'));

            throw new Error('Session expirée');
          }
        }

        const data = await response.json();

        if (!response.ok) {
          // Pour les 429 : utiliser userMessage (destiné à l'UI) plutôt que error (technique)
          const displayMessage =
            response.status === 429
              ? data.userMessage ||
                'Une erreur est survenue. Merci de réessayer dans un instant.'
              : data.error || data.message || `HTTP ${response.status}`;
          const error = new Error(displayMessage);
          (error as any).status = response.status;
          (error as any).response = data;
          throw error;
        }

        return data;
      } catch (error: any) {
        if (error.name === 'AbortError') {
          throw new Error(`Timeout après ${timeout}ms`);
        }
        throw error;
      }
    };

    return this.executeWithRetry(requestFn, retries);
  }

  // ============================================================================
  // 🔐 AUTHENTIFICATION ENTERPRISE
  // ============================================================================

  async login(credentials: { email: string; password: string }) {
    const response = await this.request<{ tokens: AuthTokens; user: AuthUser }>(
      '/api/v1/auth/login',
      {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({
          email: credentials.email.trim(),
          password: credentials.password,
          deviceInfo: {
            userAgent: navigator.userAgent,
            deviceType: 'WEB',
            deviceName: this.getDeviceName(),
            platform: navigator.platform,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        }),
      }
    );

    if (response.success && response.data?.tokens) {
      this.setTokens(
        response.data.tokens.accessToken,
        response.data.tokens.refreshToken
      );

      // Synchronisation automatique de la langue (optionnelle)
      if (
        response.data.user?.preferredLocale &&
        response.data.user.preferredLocale !== i18n.language
      ) {
        // Uncomment si vous voulez la sync automatique:
        // await i18n.changeLanguage(response.data.user.preferredLocale)
      }
    }

    return response;
  }

  async register(credentials: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    referralCode?: string;
    acceptMarketing?: boolean;
    /** Source traceable (ex: 'famille'). Stockee pour analytics, jamais
     *  utilisee pour decider d'un grant. */
    signupSource?: string;
    /** Cle de campagne consommee si valide (ex: 'famille' = 100 credits IA). */
    campaign?: string;
  }) {
    const response = await this.request<{
      user: AuthUser;
      tokens: AuthTokens;
      message: string;
      campaignBonus?: {
        credits: number;
        campaignKey: string;
        remainingSlots: number;
        expiresAt: string | null;
        expiresInDays: number | null;
      } | null;
    }>('/api/v1/auth/register', {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify({
        email: credentials.email.trim(),
        password: credentials.password,
        firstName: credentials.firstName.trim(),
        lastName: credentials.lastName.trim(),
        referralCode: credentials.referralCode || undefined,
        preferredLocale: i18n.language || 'fr-FR',
        acceptTerms: true,
        // Avant : hardcode 'false' ici, ce qui annulait silencieusement
        // la case 'recevoir les actualites' cochee en inscription. Le
        // backend stocke accepted_marketing_at quand acceptMarketing=true
        // (cf. controllers/auth/auth.ts), donc on propage juste la valeur
        // jusqu'ici. RGPD : consentement materialise au moment du clic.
        acceptMarketing: credentials.acceptMarketing === true,
        signupSource: credentials.signupSource || undefined,
        campaign: credentials.campaign || undefined,
        deviceInfo: {
          userAgent: navigator.userAgent,
          deviceType: 'WEB',
          deviceName: this.getDeviceName(),
          platform: navigator.platform,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }),
    });

    // Auto-login : le backend renvoie déjà les tokens dans data.tokens.
    // Sans ce setTokens, isAuthenticated() reste false après register et
    // tous les modals gated sur `user` (PublishChoice, SellerOnboarding,
    // OnboardingModal) ne peuvent jamais s'ouvrir → la user est redirigée
    // vers la home sans rien voir.
    if (response.success && response.data?.tokens) {
      this.setTokens(
        response.data.tokens.accessToken,
        response.data.tokens.refreshToken
      );
    }

    return response;
  }

  async socialLogin(provider: 'GOOGLE' | 'APPLE', accessToken: string) {
    // Recupere le code parrainage stocke par main.tsx au boot quand
    // l'URL contenait ?ref=CODE (ex: visite via lien /s/<userId>?ref=...).
    // Sans cette lecture, 33% des signups (Google OAuth) perdaient
    // l'attribution parrain en silence — bug confirme 23/05/2026.
    const storedReferralCode = (() => {
      try {
        return localStorage.getItem('glowp_referral_code') || undefined;
      } catch {
        return undefined;
      }
    })();

    // Audit 30/05 : avant ce fix, une maman qui visitait /famille puis
    // s'inscrivait via "Continuer avec Google" perdait l'attribution
    // campagne + ses 100 credits. On lit ici la meme attribution que le
    // register classique pour traiter Google et email a egalite.
    const { getAttributionForSignup, clearSignupAttribution } =
      await import('../lib/signupAttribution');
    const attribution = getAttributionForSignup();

    const response = await this.request<{
      tokens: AuthTokens;
      user: AuthUser;
      campaignBonus?: {
        credits: number;
        campaignKey: string;
        remainingSlots: number;
        expiresAt: string | null;
        expiresInDays: number | null;
      } | null;
    }>(`/api/v1/auth/social/${provider.toLowerCase()}`, {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify({
        provider,
        accessToken,
        deviceType: 'WEB',
        referralCode: storedReferralCode,
        ...(attribution.signupSource
          ? { signupSource: attribution.signupSource }
          : {}),
        ...(attribution.campaign ? { campaign: attribution.campaign } : {}),
      }),
    });

    // Si attribution OK, on nettoie le code pour eviter qu'un futur
    // login social du meme user le re-presente (idempotence cote serveur
    // bloque mais autant nettoyer le localStorage).
    if (response.success) {
      try {
        localStorage.removeItem('glowp_referral_code');
      } catch {
        /* ignore */
      }
      clearSignupAttribution();
      // Si une campagne a ete consommee, persiste le bonus en
      // sessionStorage pour que SignupBonusWelcome l'affiche au prochain
      // pass de rendu (meme mecanique que register classique).
      const bonus = (response.data as any)?.campaignBonus;
      if (bonus && typeof bonus.credits === 'number') {
        try {
          sessionStorage.setItem('glowp_signup_bonus', JSON.stringify(bonus));
        } catch {
          /* sessionStorage indispo */
        }
      }
    }

    if (response.success && response.data?.tokens) {
      this.setTokens(
        response.data.tokens.accessToken,
        response.data.tokens.refreshToken
      );
    }

    return response;
  }

  async verifyEmail(token: string) {
    return this.request<{ user: AuthUser; message: string }>(
      `/api/v1/auth/verify-email?token=${token}`,
      {
        method: 'GET',
        skipAuth: true,
      }
    );
  }

  async resendVerificationEmail() {
    return this.request<{ message: string }>(
      '/api/v1/auth/resend-verification',
      {
        method: 'POST',
      }
    );
  }

  async logout() {
    try {
      await this.request('/api/v1/auth/logout', {
        method: 'POST',
        timeout: 5000, // Timeout court pour logout
      });
    } catch (error) {
      console.warn('Erreur logout API:', error);
    } finally {
      this.clearTokens();
    }
  }

  async getProfile() {
    return this.request<AuthUser>('/api/v1/auth/profile');
  }

  // ============================================================================
  // 📁 UPLOAD AMÉLIORÉ AVEC PROGRESS
  // ============================================================================

  async uploadAvatar(file: File, onProgress?: (progress: number) => void) {
    return this.uploadFile(file, 'AVATAR', onProgress);
  }

  async uploadProductImages(
    files: File[],
    onProgress?: (progress: number) => void
  ) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    return this.uploadWithProgress(
      formData,
      '/api/v1/upload?type=PRODUCT',
      onProgress
    );
  }

  private async uploadFile(
    file: File,
    type: string,
    onProgress?: (progress: number) => void
  ) {
    const formData = new FormData();
    formData.append('file', file);

    return this.uploadWithProgress(
      formData,
      `/api/v1/upload?type=${type}`,
      onProgress
    );
  }

  private async uploadWithProgress(
    formData: FormData,
    endpoint: string,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Progress tracking
      if (onProgress) {
        xhr.upload.addEventListener('progress', e => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            onProgress(progress);
          }
        });
      }

      // Success handler
      xhr.addEventListener('load', () => {
        try {
          const response = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(response);
          } else {
            reject(
              new Error(
                response.error || response.message || `HTTP ${xhr.status}`
              )
            );
          }
        } catch {
          reject(new Error('Invalid JSON response'));
        }
      });

      // Error handlers
      xhr.addEventListener('error', () => reject(new Error('Network error')));
      xhr.addEventListener('timeout', () =>
        reject(new Error('Upload timeout'))
      );
      xhr.addEventListener('abort', () =>
        reject(new Error('Upload cancelled'))
      );

      // Setup request
      xhr.open('POST', `${this.baseURL}${endpoint}`);
      xhr.timeout = 120000; // 2 minutes pour uploads

      // Headers (pas de Content-Type pour FormData)
      if (this.accessToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${this.accessToken}`);
      }
      xhr.setRequestHeader('Accept-Language', i18n.language || 'fr-FR');
      xhr.setRequestHeader('X-Request-ID', crypto.randomUUID());

      // Send
      xhr.send(formData);
    });
  }

  // ============================================================================
  // 👨‍👩‍👧‍👦 PROFILS FAMILLE AVANCÉS
  // ============================================================================

  async getProfiles() {
    return this.request<UserProfile[]>('/api/v1/users/profiles');
  }

  async createProfile(data: {
    name: string;
    age?: number;
    gender?: string;
    relationship: string;
    stylePreferences?: Record<string, any>;
    sizePreferences?: Record<string, any>;
    colorPreferences?: Record<string, any>;
  }) {
    return this.request<UserProfile>('/api/v1/users/profiles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProfile(profileId: string, data: Partial<UserProfile>) {
    return this.request<UserProfile>(`/api/v1/users/profiles/${profileId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // PATCH /api/v1/users/profile — met a jour le profil du user connecte
  // (different de updateProfile qui cible un profil enfant). Utilise pour
  // les preferences globales comme acceptMarketing (RGPD opt-in differé).
  async patchMyProfile(data: {
    firstName?: string;
    lastName?: string;
    city?: string;
    countryCode?: string;
    bio?: string;
    username?: string;
    preferredLocale?: string;
    timezone?: string;
    profileVisible?: boolean;
    notificationPreferences?: unknown;
    acceptMarketing?: boolean;
  }) {
    return this.request('/api/v1/users/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteProfile(profileId: string) {
    return this.request(`/api/v1/users/profiles/${profileId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // 🛍️ PRODUITS - GESTION COMPLÈTE
  // ============================================================================

  async createProduct(data: {
    title: string;
    description: string;
    price: number;
    original_price?: number;
    productType?: 'SECONDHAND' | 'HANDMADE' | 'NEW';
    article_origin?: 'BRAND' | 'CREATOR' | 'NO_BRAND';
    creator_name?: string;
    creator_description?: string;
    brand?: string;
    condition?: string;
    size?: string;
    /** Taille machine-lisible (cm ou âge) issue du référentiel de tailles.
     *  Alimente la recherche et le matching par taille. */
    size_numeric?: number;
    /** Indication de coupe déclarée par le vendeur (taille petit/juste/grand) :
     *  'RUNS_SMALL' | 'TRUE_TO_SIZE' | 'RUNS_LARGE'. Aide l'acheteur. */
    size_fit_hint?: string;
    /** Famille de taille : 'CLOTHING' | 'SHOES' | 'ACCESSORIES' | 'BAGS'.
     *  Désambiguïse size_numeric (cm d'un vêtement vs pointure EU). */
    size_type?: string;
    color?: string;
    color2?: string;
    material?: string;
    defects_description?: string;
    category_id?: string;
    packageSize?: string;
    /** Audience cible choisie par la vendeuse via le picker "Pour qui ?".
     *  null = "objet neutre" choisi EXPLICITEMENT (le backend doit honorer
     *  ce null et ne PAS retomber sur le mapping profile). undefined =
     *  champ absent du payload (le backend peut alors deriver depuis
     *  profile). */
    target_gender?: string | null;
    /** Profil produit (ADULT_WOMAN, CHILD_GIRL, etc.). Le backend l'utilise
     *  comme fallback pour calculer target_gender si non fourni. */
    profile?: string;
    subtype?: string;
    coupe?: string;
    longueur?: string;
    manches?: string;
    col_type?: string;
    style_detail?: string;
    motif?: string;
    talon_type?: string;
    sac_taille?: string;
    status: 'ACTIVE' | 'DRAFT';
    imageUrls: string[];
    defectImageUrls?: string[];
    hasImages?: boolean;
    imageCount?: number;
    stock?: number;
    isMadeToOrder?: boolean;
    madeToOrderDays?: number;
    customizationEnabled?: boolean;
    customizationLabel?: string;
    customizationRequired?: boolean;
    customizationFields?: unknown[];
    /** Lots multi-articles. BUNDLE = lot vendu en bloc (contenu décrit dans
     *  lot_items). INDIVIDUAL = pièce d'un lot vendu pièce par pièce,
     *  reliée aux autres par lot_group_id. */
    lot_mode?: 'NONE' | 'BUNDLE' | 'INDIVIDUAL';
    lot_group_id?: string;
    lot_items?: { label: string; size: string; color: string }[];
    // Dimensions & poids personnalisés — sinon le backend estime
    length_cm?: number;
    width_cm?: number;
    height_cm?: number;
    weight_g?: number;
  }) {
    return this.request('/api/v1/products', {
      method: 'POST',
      body: JSON.stringify(data),
      // POST non-idempotent : un retry après timeout côté client crée un doublon
      // si le serveur a déjà inséré le produit. Pas de retry sur cet appel.
      retries: 1,
      // La création de produit fait beaucoup d'INSERTs en base (produit +
      // traduction + images + métadonnées) — on laisse 90s de marge en cas
      // de pic de latence Postgres pour ne pas afficher un faux timeout
      // alors que le produit est en réalité créé côté serveur.
      timeout: 90000,
    });
  }

  // ============================================================================
  // 💬 CHAT METHODS - SYSTÈME COMPLET
  // ============================================================================

  /**
   * Récupérer toutes les conversations de l'utilisateur
   */
  async getConversations() {
    return this.request<{
      success: boolean;
      data: {
        conversations: Array<{
          id: string;
          product: {
            id: string;
            title: string;
            price: number;
            images: string[];
          };
          otherUser: {
            id: string;
            firstName: string;
            lastName: string;
            avatarUrl?: string;
          };
          lastMessage?: {
            id: string;
            content: string;
            createdAt: string;
            senderId: string;
          };
          unreadCount: number;
          updatedAt: string;
        }>;
      };
    }>('/api/v1/chat/conversations', {
      method: 'GET',
    });
  }

  /**
   * Créer ou récupérer une conversation
   */
  async createConversation(
    sellerId: string,
    productId: string,
    initialMessage?: string
  ) {
    return this.request<{
      success: boolean;
      data: {
        conversation: {
          id: string;
          productId?: string;
          buyerId: string;
          sellerId: string;
        };
        reused?: boolean;
      };
    }>('/api/v1/chat/conversations', {
      method: 'POST',
      body: JSON.stringify({
        sellerId,
        productId,
        // initialMessage uniquement si explicitement fourni (ex: demande d'offre)
        // Un simple clic "Envoyer un message" n'envoie rien automatiquement
        ...(initialMessage ? { initialMessage } : {}),
      }),
    });
  }

  /**
   * Récupérer les détails d'une conversation
   */
  async getConversationDetail(conversationId: string) {
    return this.request(`/api/v1/chat/conversations/${conversationId}`, {
      method: 'GET',
    });
  }

  /**
   * Récupérer les messages d'une conversation
   */
  async getMessages(conversationId: string) {
    return this.request<{
      success: boolean;
      data: {
        messages: Array<{
          id: string;
          conversationId: string;
          senderId: string;
          content: string;
          type: string;
          createdAt: string;
          readAt?: string;
        }>;
      };
    }>(`/api/v1/chat/conversations/${conversationId}/messages`, {
      method: 'GET',
    });
  }

  /**
   * Envoyer un message
   */
  async sendMessage(
    conversationId: string,
    content: string,
    type: string = 'TEXT'
  ) {
    return this.request<{
      success: boolean;
      data: {
        message: {
          id: string;
          conversationId: string;
          senderId: string;
          content: string;
          type: string;
          createdAt: string;
        };
      };
    }>(`/api/v1/chat/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, type }),
    });
  }

  /**
   * Marquer les messages comme lus
   */
  async markConversationAsRead(conversationId: string) {
    return this.request(`/api/v1/chat/conversations/${conversationId}/read`, {
      method: 'PUT',
    });
  }

  /**
   * Traduire un message
   */
  async translateMessage(messageId: string, targetLanguage: string) {
    return this.request(`/api/v1/chat/messages/${messageId}/translate`, {
      method: 'POST',
      body: JSON.stringify({ targetLanguage }),
    });
  }

  /**
   * Envoyer une offre de prix
   */
  async sendOffer(conversationId: string, amount: number, message?: string) {
    return this.request(`/api/v1/chat/conversations/${conversationId}/offers`, {
      method: 'POST',
      body: JSON.stringify({ amount, message }),
    });
  }

  // ============================================================================
  // 🛡️ SÉCURITÉ & SESSIONS
  // ============================================================================

  async getSessions() {
    return this.request<UserSession[]>('/api/v1/auth/sessions');
  }

  async revokeSession(sessionId: string) {
    return this.request(`/api/v1/auth/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  async get2FAStatus() {
    return this.request<TwoFactorAuth>('/api/v1/auth/2fa/status');
  }

  async enable2FA(password: string) {
    return this.request<{
      qrCode: string;
      secret: string;
      backupCodes: string[];
    }>('/api/v1/auth/2fa/enable', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  // ============================================================================
  // 🔧 GESTION TOKENS & UTILITAIRES
  // ============================================================================

  setTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('glowp_access_token', accessToken);
    localStorage.setItem('glowp_refresh_token', refreshToken);
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('glowp_access_token');
    localStorage.removeItem('glowp_refresh_token');
  }

  // ============================================================================
  // 🔍 VERSION CORRIGÉE avec LOGS DEBUG - isAuthenticated()
  // ============================================================================

  isAuthenticated(): boolean {
    if (!this.accessToken) return false;

    try {
      const payload = JSON.parse(atob(this.accessToken.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      const bufferTime = 60; // 1 minute de buffer

      if (payload.exp && payload.exp < now + bufferTime) {
        this.clearTokens();
        return false;
      }

      return true;
    } catch (_error) {
      // token parsing failed — clear and return false
      this.clearTokens();
      return false;
    }
  }

  getTokenExpirationTime(): number | null {
    if (!this.accessToken) return null;

    try {
      const payload = JSON.parse(atob(this.accessToken.split('.')[1]));
      return payload.exp ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  private getDeviceName(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome Browser';
    if (ua.includes('Firefox')) return 'Firefox Browser';
    if (ua.includes('Safari')) return 'Safari Browser';
    if (ua.includes('Edge')) return 'Edge Browser';
    return 'Web Browser';
  }

  // ============================================================================
  // 🏥 TEST CONNEXION AVANCÉ
  // ============================================================================

  async testConnection(): Promise<ConnectionStatus> {
    const startTime = performance.now();

    try {
      const response = await fetch(`${this.baseURL}/health`, {
        headers: { 'Accept-Language': i18n.language || 'fr-FR' },
        signal: AbortSignal.timeout(10000),
      });

      const endTime = performance.now();
      const latency = Math.round(endTime - startTime);
      const data = await response.json();

      return {
        connected: response.ok && data.success,
        url: this.baseURL,
        message: data.success
          ? 'Backend Enterprise connecté'
          : 'Backend erreur',
        latency,
        version: data.version || '1.0.0',
        timestamp: new Date().toISOString(),
      };
    } catch (error: any) {
      const endTime = performance.now();
      const latency = Math.round(endTime - startTime);

      return {
        connected: false,
        url: this.baseURL,
        message:
          error.name === 'TimeoutError'
            ? 'Timeout connexion'
            : `Erreur: ${error.message}`,
        latency,
        timestamp: new Date().toISOString(),
      };
    }
  }

  // ============================================================================
  // 📊 ANALYTICS & TRACKING
  // ============================================================================

  async trackBehavior(action: string, data: Record<string, any>) {
    try {
      await this.request('/api/v1/analytics/track', {
        method: 'POST',
        body: JSON.stringify({
          action,
          data: {
            ...data,
            userAgent: navigator.userAgent,
            url: window.location.href,
            referrer: document.referrer,
            timestamp: new Date().toISOString(),
          },
        }),
        timeout: 5000, // Timeout court pour analytics
      });
    } catch (error) {
      // Analytics ne doit pas bloquer l'expérience utilisateur
      console.warn('Analytics tracking failed:', error);
    }
  }

  async getRecommendations(profileId?: string) {
    const params = profileId ? `?profileId=${profileId}` : '';
    return this.request(`/api/v1/recommendations${params}`);
  }

  // ============================================================================
  // 🔌 Helpers HTTP conviviaux (compatibles avec OrdersService)
  // ============================================================================
  async get<T>(
    endpoint: string,
    params?: Record<string, any>,
    options: RequestOptions = {}
  ) {
    const url = params
      ? `${endpoint}?${new URLSearchParams(
          Object.entries(params).reduce(
            (acc, [k, v]) => {
              acc[k] = String(v);
              return acc;
            },
            {} as Record<string, string>
          )
        ).toString()}`
      : endpoint;

    return this.request<T>(url, { method: 'GET', ...options });
  }

  async post<T>(endpoint: string, body?: any, options: RequestOptions = {}) {
    const isFormData =
      typeof FormData !== 'undefined' && body instanceof FormData;

    return this.request<T>(endpoint, {
      method: 'POST',
      body: isFormData ? body : JSON.stringify(body ?? {}),
      ...options,
    });
  }

  async patch<T>(endpoint: string, body?: any, options: RequestOptions = {}) {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body ?? {}),
      ...options,
    });
  }

  async put<T>(endpoint: string, body?: any, options: RequestOptions = {}) {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body ?? {}),
      ...options,
    });
  }

  async delete<T>(endpoint: string, options: RequestOptions = {}) {
    return this.request<T>(endpoint, { method: 'DELETE', ...options });
  }
}

// ============================================================================
// 🎯 INSTANCE SINGLETON EXPORTÉE
// ============================================================================

const apiClient = new ApiClient();

// Event listener pour déconnexion automatique
window.addEventListener('auth:session-expired', () => {
  // Dispatch event pour composants React
  window.dispatchEvent(new CustomEvent('auth:logout-required'));

  // Sur Capacitor (app native), ne JAMAIS faire window.location.href :
  // ça recharge toute l'app et détruit l'état React (ferme les modales en cours).
  // Le composant React écoute auth:logout-required et gère la déco proprement.
  const isCapacitor =
    typeof window !== 'undefined' &&
    (window.location.protocol === 'capacitor:' ||
      window.location.protocol === 'ionic:' ||
      !!(window as any).Capacitor);
  if (!isCapacitor) {
    setTimeout(() => {
      // 🛡️ Ne pas rediriger si un upload est en cours — évite de fermer la modale
      // de vente pendant que l'utilisateur uploade des photos depuis mobile.
      if ((window as any).__glowpActiveUpload > 0) {
        console.warn(
          '⚠️ Auth session expired but upload in progress — skipping redirect'
        );
        return;
      }
      window.location.href = '/login';
    }, 1000);
  }
});

export default apiClient;
